<script setup lang="ts">
import { computed } from "vue";
import { addressIntoGMapsLink, bemBuilder } from "@chatfood/core-utils";
import {
  AtomText,
  AtomLink,
  AtomMoney,
  useImage,
} from "@chatfood/design-system";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween.js";
import type { IStoreInfoProps } from "./props";
import { useLocale } from "~/composables/use-locale";
import { useOutletStore } from "~/stores/outlet";
import { useBusinessStore } from "~/stores/business";
import { useDiscountsStore } from "~/stores/discounts";
import { useLoyaltyProgramStore } from "~/stores/loyaltyProgram";
import { useBasketStore } from "~/stores/basket";
import type { IDomainOpeningHour } from "~/domain/opening-hours";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import type { IDomainOutletState } from "~/domain/outlet-state";
import { LoyaltyNudger } from "~/components/loyalty-nudger";
import { DiscountNudger } from "~/components/discount-nudger";
import { useDomainLabel } from "~/composables/use-domain-label";
import { useWindowResize } from "~/composables/use-window-resize";

dayjs.extend(isBetween);

const css = bemBuilder("store-info");

const { t } = useLocale();
const { outletStateMap } = useDomainLabel();

const props = defineProps<IStoreInfoProps>();

const { optimize } = useImage();

const outlet = useOutletStore();
const business = useBusinessStore();
const loyaltyProgram = useLoyaltyProgramStore();
const discounts = useDiscountsStore();
const basket = useBasketStore();

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const getDaysOfWeekStartingNextDay = (day: string) => {
  const todayIndex = DAYS_OF_WEEK.indexOf(day);

  return [
    ...DAYS_OF_WEEK.slice(todayIndex + 1),
    ...DAYS_OF_WEEK.slice(0, todayIndex + 1),
  ];
};

const setTimeToDate = (day: Dayjs, timeString: string): Dayjs => {
  const [hour, minute] = timeString.split(":");

  return day.hour(parseInt(hour)).minute(parseInt(minute)).second(0);
};

const findOpeningHoursOfToday = (openingHours: IDomainOpeningHour[]) => {
  const now = dayjs();
  const todayDayShortName = now.locale("en").format("ddd");

  const translatedDayName: Record<string, string> = {
    Mon: t("components.store_info.days_of_week.mon"),
    Tue: t("components.store_info.days_of_week.tue"),
    Wed: t("components.store_info.days_of_week.wed"),
    Thu: t("components.store_info.days_of_week.thu"),
    Fri: t("components.store_info.days_of_week.fri"),
    Sat: t("components.store_info.days_of_week.sat"),
    Sun: t("components.store_info.days_of_week.sun"),
  };

  // first we find all the opening hours of today
  const openingHoursOfToday = openingHours.filter(
    (openingHour) => openingHour.dayOfWeek === todayDayShortName,
  );

  // then we check if there is an opening hour which now inside that opening hour
  const openingHoursOfTodayBetween = openingHoursOfToday.find((openingHour) =>
    now.isBetween(
      setTimeToDate(now, openingHour.startTime),
      setTimeToDate(now, openingHour.endTime),
    ),
  );

  if (openingHoursOfTodayBetween) {
    return `${translatedDayName[todayDayShortName]} ${openingHoursOfTodayBetween.startTime}-${openingHoursOfTodayBetween.endTime}`;
  }

  // then we check if there is opening hour which now is before that opening hour
  const openingHoursOfTodayBefore = openingHoursOfToday.find((openingHour) =>
    now.isBefore(setTimeToDate(now, openingHour.startTime)),
  );

  if (openingHoursOfTodayBefore) {
    return `${translatedDayName[todayDayShortName]} ${openingHoursOfTodayBefore.startTime}-${openingHoursOfTodayBefore.endTime}`;
  }

  // if we reach this point, it means there is no opening hour available for today
  // so we start checking the next day(s)

  // getting arrays of week days starting from next day
  // eg. today is Wed, then we're looking for ["Thu", "Fri", "Sat", "Sun", "Mon", "Tue", "Wed"]
  const daysOfWeek = getDaysOfWeekStartingNextDay(todayDayShortName);

  // looping days of week to find first opening hours of the next day
  for (const day of daysOfWeek) {
    const nextDay = day;
    const openingHoursOfNextDay = openingHours
      .filter((openingHour) => openingHour.dayOfWeek === nextDay)
      .sort((openingHourA, openingHourB) =>
        openingHourA.startTime > openingHourB.startTime ? 1 : -1,
      );

    if (openingHoursOfNextDay.length) {
      return `${nextDay} ${openingHoursOfNextDay[0].startTime}-${openingHoursOfNextDay[0].endTime}`;
    }
  }

  return "";
};

const minBasket = computed(() => {
  return basket.deliveryCoverage?.minBasket;
});

const eta = computed(() => {
  const amount =
    props.orderingMode === "delivery"
      ? outlet.orderingMode.estimatedCompletionMinutes
      : outlet.orderingMode.preparationMinutes;

  return amount
    ? t("components.store_info.preparation", {
        prepStart: amount,
        prepEnd: amount + ETA_PREPARATION_MARGIN,
      })
    : undefined;
});

const busyEta = computed(() => {
  const amount =
    props.orderingMode === "delivery"
      ? outlet.orderingMode.estimatedCompletionMinutes
      : outlet.orderingMode.preparationMinutes;

  return amount
    ? t("components.store_info.busy_preparation", {
        prepStart: amount,
        prepEnd: amount + ETA_PREPARATION_MARGIN,
      })
    : undefined;
});

const etaLabel = computed(() => {
  const mapLabels: Record<IStoreInfoProps["orderingMode"], string> = {
    pickup: t("components.store_info.pickup"),
    delivery: t("components.store_info.delivery"),
    curbside: t("components.store_info.curbside"),
  };

  return mapLabels[props.orderingMode];
});

const openMapsUrl = addressIntoGMapsLink({
  lat: outlet.address.lat,
  lng: outlet.address.lng,
});

const outletStateColor: Record<
  IDomainOutletState,
  "color-mint" | "color-text-disabled" | "color-text-body"
> = {
  open: "color-mint",
  busy: "color-mint",
  closed: "color-text-disabled",
  pre_order_only: "color-text-body",
};

const { isTablet } = useWindowResize();

const displayDiscountNudger = computed(() => {
  return discounts.hasDiscounts && isTablet.value;
});

const displayLoyaltyNudger = computed(() => {
  return (
    loyaltyProgram.isEnabled && loyaltyProgram.orderAmountPercentageWorth > 0
  );
});
</script>

<template>
  <div :class="css()">
    <template v-if="business.cover">
      <div
        :class="css('cover-mobile')"
        data-test="cover-image"
        :style="`background-image: url(${optimize(business.cover, { w: 1100, fit: 'crop', crop: 'top' })});`"
      />
      <div
        :class="css('cover')"
        data-test="cover-image"
        :style="`background-image: url(${optimize(business.cover, { w: 1100, fit: 'crop', crop: 'top' })});`"
      />
    </template>
    <div :class="css('content-wrapper')">
      <div
        data-test="content"
        :class="{
          [css('content')]: true,
          [css('content', 'with-discount')]: discounts.hasDiscounts,
        }"
      >
        <div :class="css('address')" data-test="address-component">
          <AtomText type="small" color="color-text-secondary">
            {{ t("components.store_info.address_label") }}
          </AtomText>
          <div :class="css('address-line')">
            <AtomText type="body-bold">
              {{ outlet.name }}
            </AtomText>
            <AtomText>{{ outlet.address.location }}</AtomText>
            <AtomLink :to="openMapsUrl" target="_blank">
              {{ t("components.store_info.open_maps_label") }}
            </AtomLink>
          </div>
        </div>
        <div :class="css('order-detail')">
          <div :class="css('opening-hours')">
            <AtomText
              type="small"
              data-test="opening-hour"
              color="color-text-secondary"
            >
              {{ findOpeningHoursOfToday(outlet.orderingMode.openingHours) }}
            </AtomText>
            <AtomText
              type="body-bold"
              :color="outletStateColor[outlet.orderingMode.outletState]"
            >
              {{ outletStateMap[outlet.orderingMode.outletState] }}
            </AtomText>
          </div>
          <div v-if="eta" :class="css('eta')" data-test="eta">
            <AtomText type="small" color="color-text-secondary">
              {{ etaLabel }}
            </AtomText>
            <AtomText
              v-if="outlet.isBusy"
              type="body-bold"
              color="color-canary"
              >{{ busyEta }}</AtomText
            >
            <AtomText v-else type="body-bold">{{ eta }}</AtomText>
          </div>
          <div
            v-if="minBasket"
            data-test="min-basket"
            :class="css('min-basket')"
          >
            <AtomText type="small" color="color-text-secondary">
              {{ t("components.store_info.min_basket_label") }}
            </AtomText>
            <AtomText type="body-bold">
              <template v-if="minBasket.value === 0">
                {{ t("components.store_info.min_basket_no_value") }}
              </template>
              <AtomMoney
                v-else
                data-test="min-basket-value"
                :amount="minBasket.value"
                :currency="minBasket.currency"
              />
            </AtomText>
          </div>
        </div>
      </div>
      <DiscountNudger
        v-if="discounts.hasDiscounts"
        :class="css('discount-desktop')"
      />
    </div>
    <div
      v-if="displayDiscountNudger || displayLoyaltyNudger"
      :class="{
        [css('claims-area')]: true,
        [css('claims-area', 'only-mobile')]: !loyaltyProgram.isEnabled,
      }"
    >
      <DiscountNudger
        v-if="displayDiscountNudger"
        :class="css('discount-mobile')"
      />
      <LoyaltyNudger v-if="displayLoyaltyNudger" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.store-info {
  background-color: var(--color-base-100);
  border-radius: var(--radii-card-md);

  &__cover-mobile {
    width: 100%;
    aspect-ratio: 16 / 9;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    @media (width > $breakpoint-sm) {
      display: none;
    }
  }

  &__cover {
    display: none;
    width: 100%;
    aspect-ratio: 16 / 5;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-color: var(--color-base-90);
    border-radius: var(--radii-card-md);

    @media (width > $breakpoint-sm) {
      display: block;
    }
  }

  &__content-wrapper {
    display: flex;
    gap: var(--spacing-lg);
    align-items: flex-start;
    padding: var(--spacing-md);

    @media (width > $breakpoint-md) {
      padding: var(--spacing-lg);
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-sm);
    width: 100%;

    @media (width > $breakpoint-lg) {
      flex-direction: row;
      gap: var(--spacing-2xl);

      &--with-discount {
        flex-direction: column;
        gap: var(--spacing-sm);
      }
    }
  }

  &__address,
  &__eta,
  &__opening-hours,
  &__min-basket {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    flex: 1 0 0;
  }

  &__order-detail {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: var(--spacing-sm);
    flex: 1 0 0;
    flex-wrap: wrap;

    > div {
      min-width: 100px;
    }
  }

  &__address-line {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-sm);
  }

  &__discount-desktop {
    display: none;
    max-width: 100%;

    @media (width > $breakpoint-lg) {
      display: flex;
      max-width: 350px;
    }
  }

  &__claims-area {
    display: flex;
    gap: var(--spacing-md);
    flex-direction: column;
    padding: var(--spacing-md);
    border-top: 1px solid var(--color-base-95);

    @media (width > $breakpoint-md) {
      padding: var(--spacing-lg);

      &--only-mobile {
        display: none;
      }
    }
  }
}
</style>
